import { useEffect, useRef } from 'react';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { Node } from '../../../core/models/scene';

/**
 * Swimlane-style drag for Compass container shapes.
 *
 * When a container (`attrs.isContainer === true`) moves, every
 * descendant that carries `attrs.compassParentId === <container.id>`
 * moves by the same delta. Subnets inside VPCs and resources inside
 * subnets cascade transitively because the subnet is itself a
 * container and will trigger its own cascade in the next tick.
 *
 * Implementation uses a Zustand store subscription keyed on
 * `state.nodes`. A re-entrancy guard prevents the sync writes from
 * looping back through the same subscription.
 *
 * Pure React hook — mount once near the top of the canvas tree.
 */
export function useCompassContainerDragSync(): void {
  // Keep the latest positions we've observed so we can compute a
  // delta when the store emits a new nodes snapshot. Map values are
  // the position we last *wrote* (or last saw from an external
  // change) — never updated from inside the cascade.
  const prevPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const syncingRef = useRef(false);
  const initialisedRef = useRef(false);

  useEffect(() => {
    // Seed the positions map from current state so the very first
    // subscription tick doesn't see every node as "moved".
    const seed = useDiagramStore.getState().nodes;
    prevPositionsRef.current = new Map(
      seed.map((n) => [n.id, { x: num(n.x), y: num(n.y) }]),
    );
    initialisedRef.current = true;

    const unsubscribe = useDiagramStore.subscribe((state) => {
      if (!initialisedRef.current) return;
      if (syncingRef.current) return;

      const nodes = state.nodes;
      const deltas: { id: string; dx: number; dy: number }[] = [];
      const freshPositions = new Map<string, { x: number; y: number }>();

      for (const n of nodes) {
        const cur = { x: num(n.x), y: num(n.y) };
        freshPositions.set(n.id, cur);
        const prev = prevPositionsRef.current.get(n.id);
        if (!prev) continue;
        const dx = cur.x - prev.x;
        const dy = cur.y - prev.y;
        if (dx === 0 && dy === 0) continue;
        const attrs = (n as Node & { attrs?: Record<string, unknown> }).attrs;
        if (!attrs?.isContainer) continue;
        deltas.push({ id: n.id, dx, dy });
      }

      if (deltas.length === 0) {
        prevPositionsRef.current = freshPositions;
        return;
      }

      syncingRef.current = true;
      try {
        const moveNode = useDiagramStore.getState().moveNode;
        // Build a parent lookup so we can find descendants in one pass.
        const childrenByParent = new Map<string, Node[]>();
        for (const n of nodes) {
          const parentId = (n as Node & { attrs?: Record<string, unknown> })
            .attrs?.compassParentId;
          if (typeof parentId !== 'string') continue;
          const arr = childrenByParent.get(parentId) ?? [];
          arr.push(n);
          childrenByParent.set(parentId, arr);
        }

        for (const { id, dx, dy } of deltas) {
          cascade(id, dx, dy, childrenByParent, moveNode, freshPositions);
        }
      } finally {
        syncingRef.current = false;
      }

      prevPositionsRef.current = freshPositions;
    });

    return () => {
      unsubscribe();
    };
  }, []);
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function cascade(
  parentId: string,
  dx: number,
  dy: number,
  childrenByParent: Map<string, Node[]>,
  moveNode: (id: string, pos: { x: number; y: number }) => void,
  freshPositions: Map<string, { x: number; y: number }>,
): void {
  const children = childrenByParent.get(parentId);
  if (!children) return;
  for (const child of children) {
    const cur = freshPositions.get(child.id) ?? { x: num(child.x), y: num(child.y) };
    const next = { x: cur.x + dx, y: cur.y + dy };
    // Update both the store and the fresh-positions record so
    // recursion sees the child at its post-cascade position.
    moveNode(child.id, next);
    freshPositions.set(child.id, next);
    // Recurse — if this child is itself a container, its own
    // descendants cascade too.
    const attrs = (child as Node & { attrs?: Record<string, unknown> }).attrs;
    if (attrs?.isContainer) {
      cascade(child.id, dx, dy, childrenByParent, moveNode, freshPositions);
    }
  }
}
